import { getUrlFromRoute } from "@pathwright/ui/src/components/link/utils"
import CoverPageContainer from "@pathwright/web/src/modules/cover-pages/CoverPageContainer"
import { immersiveRoutes } from "@pathwright/web/src/modules/cover-pages/immersiveRouteConfig"
import { getResourceSlugId } from "@pathwright/web/src/modules/utils/urls"
import { getDefaultLaunchedFromLibrary } from "lib/core/routing/utils"
import { loadResource } from "path/middleware"
import { useCallback } from "react"
import StoreOrLibraryBrowse from "../../store/StoreOrLibraryBrowse"
import CoverPageRegisterAdapter from "./CoverPageRegisterAdapter"

// Register route onEnter: ensure both resource-store and register-store are loaded
function handleRegisterMiddleware(nextState, replace) {
  require("library/show/resource-store")
  require("library/register/register-store")
  return loadResource(nextState, replace)
}

// Wrapper component that renders CoverPageContainer and conditionally overlays immersive content
const CoverPageWrapper = (props) => {
  const { match, params: sectionParams } = props
  const params = sectionParams || match.params
  const { resourceSlug, cohortId } = params

  const resourceId = getResourceSlugId(resourceSlug)

  const currentPathPattern = match?.path || ""

  // We support urls with and without cohortId
  const baseCover = cohortId
    ? `/${resourceSlug}/${cohortId}/cover`
    : `/${resourceSlug}/cover`

  const handleOpenImmersive = useCallback(
    (route) => {
      const baseCoverLocal = cohortId
        ? `/${resourceSlug}/${cohortId}/cover`
        : `/${resourceSlug}/cover`
      const immersiveRouteConfig = immersiveRoutes.find(
        (r) => r.key === route.name
      )
      const path = immersiveRouteConfig?.getPath
        ? immersiveRouteConfig.getPath({ baseCover: baseCoverLocal, route })
        : null
      if (path) window.App.navigate(path)
    },
    [cohortId, resourceSlug]
  )

  // The register route needs its own render using legacy components
  const renderRegisterContent = useCallback(
    () => <CoverPageRegisterAdapter {...props} />,
    [props]
  )

  const activeRouteConfig = immersiveRoutes.find((r) =>
    r.isActive({ matchPath: currentPathPattern, params })
  )
  const activeImmersiveRoute = activeRouteConfig?.getImmersiveRoute
    ? activeRouteConfig.getImmersiveRoute(params)
    : null
  const isImmersive = !!activeRouteConfig

  const handleCloseToCover = useCallback(() => {
    window.App.navigate(`${baseCover}/`)
  }, [baseCover])

  const handleClose = useCallback(() => {
    if (isImmersive) {
      handleCloseToCover()
    } else {
      window.App.navigate(`${props.modalLaunchedFrom?.pathname || "/library/"}`)
    }
  }, [props.modalLaunchedFrom?.pathname, isImmersive])

  return (
    <>
      <CoverPageContainer
        resourceId={resourceId}
        onClose={handleClose}
        immersiveRoute={activeImmersiveRoute || null}
        renderRegisterContent={renderRegisterContent}
        onOpenImmersive={handleOpenImmersive}
      />
    </>
  )
}

// Main cover page route structure
const coverPageRoutes = {
  path: "/:resourceSlug/:cohortId(\\d+)?/cover/",
  components: {
    primary: StoreOrLibraryBrowse,
    chakraModal: CoverPageWrapper
  },
  getDefaultLaunchedFrom: getDefaultLaunchedFromLibrary,
  onClose: (nextState) => {
    const launchedFrom = getUrlFromRoute(nextState.modalLaunchedFrom)
    window.App.navigate(
      launchedFrom || getDefaultLaunchedFromLibrary(nextState)
    )
  }
}

const toArray = (v) => (Array.isArray(v) ? v : [v])

coverPageRoutes.childRoutes = immersiveRoutes.reduce((acc, route) => {
  const patterns = toArray(route.pathRe)
  patterns.forEach((path) => {
    acc.push({
      path,
      components: {
        chakraModal: CoverPageWrapper
      },
      onEnter: route.key === "register" ? handleRegisterMiddleware : null,
      onClose: (nextState) => {
        const { resourceSlug, cohortId } = nextState.match.params
        const baseCover = cohortId
          ? `/${resourceSlug}/${cohortId}/cover/`
          : `/${resourceSlug}/cover/`
        window.App.navigate(`${baseCover}`)
      },
      isFullscreen: true
    })
  })
  return acc
}, [])

// Support deprecated /library/* cover page routes
export const libraryCoverPageRoutes = {
  ...coverPageRoutes,
  path: "/library" + coverPageRoutes.path
}

export default coverPageRoutes
